<template>
    <page :tabs="tabs" :busy="busy" :show-nav="!isMobile">
        <div v-if="isMobile" class="mt-1" />
        <grid
            ref="grid"
            :header-height="45"
            :column-defs="columnDefs"
            :row-data="rowData"
            :get-row-style="getRowStyle"
            :pinned-bottom-row-data="pinnedBottomRowData"
            :is-auto-width="true"
            :is-search="false"
            :statusBar="statusBar"
            grid-theme="ag-theme-alpine-report"
        >
            <date-range-picker
                v-if="!queryParams"
                :range-options="predefinedRanges"
                :value="dateRange"
                :date-format="userUnicodeDateFormat"
                @input="handleDaterangeChange"
            />
            <export-to-excel-button
                :href="exportExcelHref"
            ></export-to-excel-button>
            <report-settings-modal
                :modalId="reportSettingsModalId"
                :initialValues="reportSettings"
                :excelOnly="true"
            />
        </grid>
    </page>
</template>

<script>
import { mapGetters } from 'vuex';
import format from 'date-fns/format';
import API from '../../api';
import Page from '../generic/page/page.vue';
import Grid from '../generic/grid/grid.vue';
import ExportToExcelButton from '../generic/export-to-excel-button/export-to-excel-button.vue';
import DateRangePicker, {
    nameToRange,
    TODAY,
    YESTERDAY,
    THIS_MONTH,
    LAST_MONTH,
    TOMORROW,
    THIS_WEEK,
    NEXT_WEEK,
    NEXT_MONTH,
    THIS_YEAR,
    LAST_WEEK,
    LAST_YEAR,
} from '../generic/date-range-picker/date-range-picker.vue';
import { REPORT_GUEST_REGISTER_ENDPOINT } from '../../endpoints';
import ReportSummaryBar from './report-summary-bar.vue';
import { removeScrollMixin } from '../../mixins/remove-scroll-mixin';
import ReportSettingsModal, {
    DATE_PERIODS_INPUT,
} from './report-settings/report-settings-modal.vue';

export default {
    name: 'ReportCancellation',
    components: {
        Page,
        Grid,
        DateRangePicker,
        reportSummaryBar: ReportSummaryBar,
        ExportToExcelButton,
        ReportSettingsModal,
    },
    mixins: [removeScrollMixin],
    data() {
        return {
            title: 'Guest Register',
            tabs: [
                {
                    title: 'Guest Register',
                    route: '/app/reports/cancellation/',
                    active: true,
                },
            ],
            dateRange: nameToRange(THIS_MONTH),
            predefinedRanges: [
                YESTERDAY,
                TODAY,
                TOMORROW,
                LAST_WEEK,
                THIS_WEEK,
                NEXT_WEEK,
                LAST_MONTH,
                THIS_MONTH,
                NEXT_MONTH,
                THIS_YEAR,
                LAST_YEAR
            ],
            dateRequestFormat: 'yyyy-MM-dd',
            busy: false,
            gridApi: null,
            rowData: [],
            getRowStyle: null,
            windowWidth: 0,
            windowHeight: 0,
            statusBar: null,
            totals: {},
            taxesTotal: {},
            taxesHeader: [],
            queryParams: null,
            reportSettingsModalId: 'report-settings-modal-id',
        };
    },
    computed: {
        ...mapGetters('header', [
            'userUnicodeDateFormat',
        ]),
        pinnedBottomRowData() {
            const data = this.totals;
            this.taxesHeader.forEach((header, index) => {
                data[header] = this.taxesTotal[index];
            })
            return [data]
        },
        columnDefs() {
            return [
                {
                    headerName: '#',
                    valueGetter: (params) => {
                        return params.node.rowPinned ? this.rowData.length : params.node.rowIndex + 1
                    },
                    minWidth: 40,
                },
                {
                    field: 'date',
                    headerName: 'Date',
                    type: 'dateColumn',
                    minWidth: 120,
                },
                {
                    field: 'invoice',
                    headerName: 'Invoice',
                    minWidth: 80,
                },
                {
                    headerName: 'Unit Name',
                    field: 'unit_name',
                    minWidth: 100,
                },
                {
                    headerName: 'Unit #',
                    field: 'unit_number',
                    minWidth: 60,
                },
                {
                    headerName: 'First Name',
                    field: 'first_name',
                    minWidth: 100,
                },
                {
                    headerName: 'Last Name',
                    field: 'last_name',
                    minWidth: 100,
                },
                {
                    field: 'email',
                    headerName: 'Email',
                    minWidth: 100,
                },
                {
                    field: 'country',
                    headerName: 'Country',
                    minWidth: 80,
                },
                {
                    field: 'rental_revenue',
                    headerName: 'Rental Revenue',
                    type: 'amountColumn',
                    minWidth: 100,
                },
                {
                    field: 'service_revenue',
                    headerName: 'Service Revenue',
                    type: 'amountColumn',
                    minWidth: 100,
                },
                {
                    field: 'fee_revenue',
                    headerName: 'Fee Revenue',
                    type: 'amountColumn',
                    minWidth: 100,
                },
                {
                    field: 'total_revenue',
                    headerName: 'Total Revenue',
                    type: 'amountColumn',
                    minWidth: 100,
                },
                ...this.taxesHeader.map(header => {
                    return {
                        headerName: header,
                        field: header,
                        type: 'amountColumn',
                        minWidth: 120,
                    }
                }),
                {
                    field: 'total_tax',
                    headerName: 'Total Tax Collected',
                    type: 'amountColumn',
                    minWidth: 100,
                    autoWidthExclude: true
                },
            ];
        },
        exportExcelHref() {
            let url = '/app/reports/guest-register/row-data/excel/';
            if (this.queryParams) {
                const { from_date: fromDate, to_date: toDate, pk, tax_name: taxName } = this.queryParams
                url += `?from_date=${fromDate}`;
                url += `&to_date=${toDate}`;
                url += `&pk=${pk}`;
                url += `&tax_name=${taxName}`;
            } else {
                url += `?from_date=${this.startDateRequest}`;
                url += `&to_date=${this.endDateRequest}`;
                url += `&pk=`;
                url += `&tax_name=`;
            }
            return url;
        },
        startDateRequest() {
            return format(this.dateRange.from, this.dateRequestFormat);
        },
        endDateRequest() {
            return format(this.dateRange.to, this.dateRequestFormat);
        },
        isMobile() {
            const md = 768;
            return this.windowWidth < md || this.windowHeight < md;
        },
        reportSettings() {
            return {
                name: 'Guest Register',
                report_sysname: 'GUEST_REGISTER',
                settings: [
                    {
                        type: DATE_PERIODS_INPUT,
                        value: 'this_month',
                        label: 'Date Range',
                        name: 'date_range',
                        predefinedRanges: this.predefinedRanges
                    },
                    {
                        value: '',
                        name: 'pk',
                        type: '',
                    },
                     {
                        value: '',
                        name: 'tax_name',
                        type: '',
                    },
                ],
            };
        },
    },
    mounted() {
        if (this.$route.query.pk) {
            this.queryParams = this.$route.query
        }
        this.fetchData();
        const grid = this.$refs.grid;
        this.gridApi = grid.gridOptions.api;
        this.onResize();
        window.addEventListener('resize', this.onResize);
    },
    beforeUnmount() { 
        window.removeEventListener('resize', this.onResize);
    },
    beforeMount() {
        this.getRowStyle = (params) => {
            if (params.node.rowPinned) {
                return { 'font-weight': 'bold' };
            }
        };
        this.statusBar = {
            statusPanels: [
                {
                    key: 'reportSummaryBar',
                    statusPanel: 'reportSummaryBar',
                    align: 'left',
                },
            ],
        };
    },
    methods: {
        async fetchData() {
            try {
                this.busy = true;
                const { data } = await API.get(
                    REPORT_GUEST_REGISTER_ENDPOINT(),
                    {
                        params: !this.queryParams ? {
                            from_date: this.startDateRequest,
                            to_date: this.endDateRequest,
                            pk: '',
                            tax_name: '',
                        } : {...this.queryParams}
                    }
                );
                const { results, taxes_header: taxesHeader, totals, taxes_total: taxesTotal } = data;
                this.taxesHeader = taxesHeader;
                this.totals = totals;
                this.taxesTotal = taxesTotal;
                this.rowData = results.map((result, index) => {
                    const { taxes } = result;
                    const data = {
                        id: index,
                        ...result,
                    }
                    taxesHeader.forEach((header, index) => {
                        data[header] = taxes[index];
                    })
                    return data;
                });
            } catch (e) {
                console.error(e)
                this.errorToast({ text: "Could not fetch data" });
            } finally {
                this.busy = false
            }
        },
        onResize() {
            this.windowWidth = window.innerWidth;
            this.windowHeight = window.innerHeight;
            const statusBarComponent = this.gridApi.getStatusPanel('reportSummaryBar');
            let componentInstance = statusBarComponent;
            if (statusBarComponent.getFrameworkComponentInstance) {
                componentInstance = statusBarComponent.getFrameworkComponentInstance();
            }
            componentInstance.setVisible(this.isMobile);
            componentInstance.setTitle(this.title);
        },
        handleDaterangeChange(value) {
            this.dateRange = { ...value };
            this.fetchData();
        },
    },
};
</script>
